/**
 * StatCard Component
 * 
 * Reusable card component for displaying travel statistics
 */

import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { Heading, Body, Caption } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { CircularProgress } from '@/components/expenses/CircularProgress';

type StatCardProps = {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: keyof typeof Ionicons.glyphMap;
  iconColor?: string;
  trend?: {
    value: number;
    label: string;
    percentage?: number; // Percentage change
  };
  hero?: boolean; // Hero variant - larger, more prominent
  progress?: {
    current: number;
    target: number;
    showRing?: boolean;
  };
};

export function StatCard({
  title,
  value,
  subtitle,
  icon,
  iconColor,
  trend,
  hero = false,
  progress,
}: StatCardProps) {
  const theme = useTheme();
  const iconSize = hero ? 32 : 24;
  const iconContainerSize = hero ? 64 : 48;
  const valueFontSize = hero ? 48 : 32;
  const stampColor = iconColor || theme.colors.accent;

  const progressPercentage = progress
    ? Math.min((progress.current / progress.target) * 100, 100)
    : undefined;

  const trendColor = trend?.percentage
    ? trend.percentage > 0
      ? theme.colors.success
      : trend.percentage < 0
      ? theme.colors.error
      : theme.colors.textSecondary
    : theme.colors.success;

  return (
    <Card
      variant="elevated"
      elevation={hero ? 2 : 1}
      padding={hero ? 20 : 16}
      style={[styles.card, hero && styles.heroCard]}
    >
      <View style={styles.header}>
        {/* Stamp-style badge (outlined ring + subtle fill) */}
        <View
          style={[
            styles.stampOuter,
            hero && styles.heroStampOuter,
            {
              width: iconContainerSize,
              height: iconContainerSize,
              borderRadius: iconContainerSize / 2,
              borderColor: stampColor + '70',
              backgroundColor: theme.colors.surface,
            },
          ]}
        >
          {/* Postmark texture (subtle: extra ring + slash) */}
          <View
            pointerEvents="none"
            style={[
              styles.postmarkRing,
              {
                width: iconContainerSize,
                height: iconContainerSize,
                borderRadius: iconContainerSize / 2,
                borderColor: stampColor + '35',
              },
            ]}
          />
          <View
            pointerEvents="none"
            style={[
              styles.postmarkSlash,
              {
                backgroundColor: stampColor + '22',
                width: Math.round(iconContainerSize * 0.9),
                left: Math.round(iconContainerSize * 0.05),
                top: Math.round(iconContainerSize * 0.62),
              },
            ]}
          />
          <View
            style={[
              styles.stampInner,
              {
                width: iconContainerSize - (hero ? 14 : 12),
                height: iconContainerSize - (hero ? 14 : 12),
                borderRadius: (iconContainerSize - (hero ? 14 : 12)) / 2,
                borderColor: stampColor + '45',
                backgroundColor: stampColor + '12',
              },
            ]}
          >
            <Ionicons name={icon} size={iconSize} color={stampColor} />
          </View>
        </View>
        {trend && (
          <View style={styles.trend}>
            <View style={styles.trendRow}>
              <Ionicons
                name={trend.percentage && trend.percentage > 0 ? 'trending-up' : trend.percentage && trend.percentage < 0 ? 'trending-down' : 'remove'}
                size={14}
                color={trendColor}
                style={styles.trendIcon}
              />
              <Caption style={[styles.trendText, { color: trendColor }]}>
                {trend.percentage !== undefined
                  ? `${trend.percentage > 0 ? '+' : ''}${trend.percentage.toFixed(0)}%`
                  : `${trend.value > 0 ? '+' : ''}${trend.value}`}
              </Caption>
            </View>
            {trend.label && (
              <Caption style={[styles.trendLabel, { color: theme.colors.textTertiary }]}>
                {trend.label}
              </Caption>
            )}
          </View>
        )}
      </View>
      <View style={styles.content}>
        {progress?.showRing ? (
          <View style={styles.progressContainer}>
            <CircularProgress
              size={hero ? 120 : 100}
              progress={progressPercentage || 0}
              color={iconColor || theme.colors.accent}
              strokeWidth={hero ? 10 : 8}
            >
              <View style={styles.progressContent}>
                <Heading
                  style={[
                    styles.value,
                    styles.progressValue,
                    hero ? styles.progressValueHero : styles.progressValueRegular,
                  ]}
                >
                  {value}
                </Heading>
                <Caption style={[styles.progressSubtext, { color: theme.colors.textTertiary }]}>
                  {progress.current} / {progress.target}
                </Caption>
              </View>
            </CircularProgress>
          </View>
        ) : (
          <Heading style={[styles.value, { fontSize: valueFontSize, lineHeight: valueFontSize + 8 }]}>
            {value}
          </Heading>
        )}
        <Body style={[styles.title, { color: theme.colors.textSecondary }]}>
          {title}
        </Body>
        {subtitle && (
          <Caption style={[styles.subtitle, { color: theme.colors.textTertiary }]}>
            {subtitle}
          </Caption>
        )}
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1,
    minWidth: 150,
  },
  heroCard: {
    width: '100%',
    marginBottom: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  stampOuter: {
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderStyle: 'dashed',
    position: 'relative',
    overflow: 'hidden',
  },
  heroStampOuter: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 6,
    elevation: 3,
  },
  stampInner: {
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1.5,
  },
  postmarkRing: {
    position: 'absolute',
    borderWidth: 1.5,
    borderStyle: 'dotted',
    opacity: 0.9,
    transform: [{ rotate: '-14deg' }],
  },
  postmarkSlash: {
    position: 'absolute',
    height: 2,
    borderRadius: 999,
    transform: [{ rotate: '-12deg' }],
  },
  trend: {
    alignItems: 'flex-end',
  },
  trendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  trendIcon: {
    marginRight: 2,
  },
  trendText: {
    fontSize: 12,
    fontWeight: '600',
  },
  trendLabel: {
    fontSize: 10,
    marginTop: 2,
  },
  content: {
    flex: 1,
  },
  value: {
    fontWeight: '700',
    marginBottom: 4,
  },
  progressValue: {
    marginBottom: 0,
  },
  progressValueRegular: {
    fontSize: 28,
    lineHeight: 36,
  },
  progressValueHero: {
    fontSize: 36,
    lineHeight: 44,
  },
  title: {
    fontSize: 14,
    fontWeight: '500',
  },
  subtitle: {
    fontSize: 12,
    marginTop: 4,
  },
  progressContainer: {
    alignItems: 'center',
    marginBottom: 8,
  },
  progressContent: {
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ translateY: 2 }],
  },
  progressSubtext: {
    fontSize: 11,
    marginTop: 2,
    fontWeight: '500',
  },
});

